import React, { useContext, useEffect, useState } from 'react';
import { BubbleOverlay, BubbleAlert, BubbleButton } from '@go-client/components/BubbleUI';
import { Property } from '@go-shared/types/property';
import { usePropertyInfo } from './PropertyInfoContext';
import { safeIdCompare } from '@go-shared/lib/utils';
import { unsetStorage } from '@go-client/lib/storage';
import { resolveHomePath } from '@go-client/routes';
export interface ActivePropertyBundle {
  activeProperty: Property;
  refreshContext: () => Promise<void>;
}

const ActivePropertyContext = React.createContext<ActivePropertyBundle | undefined>(undefined);

export function useActiveProperty() {
  const context = useContext(ActivePropertyContext);

  if (context === undefined) {
    throw new Error('useActiveProperty must be used within a ActivePropertyProvider');
  }

  return context;
}

interface WithActivePropertyPropsBase {
  property?: string;
}

type WithActivePropertyProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'property'> &
  WithActivePropertyPropsBase;

export function WithActiveProperty(props: WithActivePropertyProps) {
  const { property, className: propClasses, children, ...rest } = props;

  const [initialLoading, setInitialLoading] = useState(true);
  const [activePropertyBundle, setActivePropertyBundle] = useState<
    ActivePropertyBundle | undefined
  >(undefined);

  const { myProperties, refreshContext: refreshPropertyInfo } = usePropertyInfo();

  useEffect(() => {
    const activeProperty = props.property
      ? myProperties.find(p => safeIdCompare(p._id, props.property))
      : undefined;
    setActivePropertyBundle(
      activeProperty
        ? {
            activeProperty,
            refreshContext: () => refreshPropertyInfo({ specificPropertyId: activeProperty._id }),
          }
        : undefined
    );
    setInitialLoading(false);
  }, [myProperties, props.property]);

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  if (!initialLoading && !activePropertyBundle?.activeProperty) {
    // this means the user intents to visit a url that does not exist
    // this can happen via the shortcut on the menu bar that leads to
    // today's daily plan. since its possible that its incorrect, we
    // clear it out so that the user does not take the same course
    unsetStorage('currentSailingUrl');
    unsetStorage('lastPropertyId');
  }

  return (
    <BubbleOverlay loading={initialLoading} {...rest} className={classes.join(' ')}>
      {!initialLoading && (
        <>
          {activePropertyBundle && activePropertyBundle.activeProperty ? (
            <ActivePropertyProvider activePropertyBundle={{ ...activePropertyBundle }}>
              {props.children}
            </ActivePropertyProvider>
          ) : (
            <BubbleAlert variant="crimson" className="rounded-0-i">
              Could not find property referenced in the URL.
              <BubbleButton
                linkProps={{ to: resolveHomePath() }}
                variant="white"
                inverse
                className="p-0-i underline ml-1"
              >
                Click Here to Go Home
              </BubbleButton>
            </BubbleAlert>
          )}
        </>
      )}
    </BubbleOverlay>
  );
}

interface ActivePropertyProviderPropsBase {
  activePropertyBundle: ActivePropertyBundle;
}

type ActivePropertyProviderProps = ActivePropertyProviderPropsBase & { children?: React.ReactNode };

export default function ActivePropertyProvider(props: ActivePropertyProviderProps) {
  const { activePropertyBundle, children } = props;

  return (
    <ActivePropertyContext.Provider value={activePropertyBundle}>
      {children}
    </ActivePropertyContext.Provider>
  );
}
